import {
  argbFromHex,
  blueFromArgb,
  greenFromArgb,
  Hct,
  hexFromArgb,
  redFromArgb,
  TonalPalette
} from "@material/material-color-utilities";

import {
  DARK_THEME_ASSIGNMENTS,
  FIXED_NEUTRAL_MONOCHROME_SEED_COLOR,
  FIXED_UTILITY_SEED_COLORS,
  LIGHT_THEME_ASSIGNMENTS,
  NEUTRAL_SEED_COLOR_CHROMA
} from "./consts";
import {
  ColorRole,
  ContrastLevel,
  ContrastRange,
  FixedNeutralMonochromeSeedColor,
  FixedUtilitySeedColor,
  Mode,
  Palette,
  RGBA,
  SeedColors,
  ThemeColorAssignments,
  ThemeColors,
  VariableSeedColors
} from "./types";

export function baseColorHexToRGBA(baseColorHex: string): RGBA {
  const baseColorARGB = argbFromHex(baseColorHex);

  return {
    r: redFromArgb(baseColorARGB),
    g: greenFromArgb(baseColorARGB),
    b: blueFromArgb(baseColorARGB),
    a: 1
  };
}

export function overlayColorHexToRGBA(
  overlayColorHex: string,
  overlayOpacity: number
): RGBA {
  const overlayColorARGB = argbFromHex(overlayColorHex);

  return {
    r: redFromArgb(overlayColorARGB),
    g: greenFromArgb(overlayColorARGB),
    b: blueFromArgb(overlayColorARGB),
    a: overlayOpacity
  };
}

export function RGBAToString(RGBA: RGBA): string {
  return `rgba(${RGBA.r}, ${RGBA.g}, ${RGBA.b}, ${RGBA.a})`;
}

export function RGBAToHex(RGBA: RGBA) {
  const { r, g, b, a } = RGBA;

  const rHex = r.toString(16).padStart(2, "0");
  const gHex = g.toString(16).padStart(2, "0");
  const bHex = b.toString(16).padStart(2, "0");

  if (a === 1) return "#" + rHex + gHex + bHex;

  const aHex = Math.round(a * 255)
    .toString(16)
    .padStart(2, "0");

  return "#" + rHex + gHex + bHex + aHex;
}

function generateRequiredContrastLevelFromSeedColor(
  requiredContrastLevel: ContrastLevel,
  seedColorHex: string
) {
  // Note: hexFromArgb and argbFromHex don't work correctly with 8 digit hex values (i.e. values that include an alpha channel)
  // See https://github.com/material-foundation/material-color-utilities/issues/33

  return hexFromArgb(
    TonalPalette.fromInt(argbFromHex(seedColorHex)).tone(
      parseInt(requiredContrastLevel)
    )
  );
}

export function generateContrastRangeFromSeedColor(
  seedColorHex: string
): ContrastRange {
  return {
    "100": generateRequiredContrastLevelFromSeedColor("100", seedColorHex),
    "99": generateRequiredContrastLevelFromSeedColor("99", seedColorHex),
    "98": generateRequiredContrastLevelFromSeedColor("98", seedColorHex),
    "95": generateRequiredContrastLevelFromSeedColor("95", seedColorHex),
    "90": generateRequiredContrastLevelFromSeedColor("90", seedColorHex),
    "80": generateRequiredContrastLevelFromSeedColor("80", seedColorHex),
    "70": generateRequiredContrastLevelFromSeedColor("70", seedColorHex),
    "60": generateRequiredContrastLevelFromSeedColor("60", seedColorHex),
    "50": generateRequiredContrastLevelFromSeedColor("50", seedColorHex),
    "40": generateRequiredContrastLevelFromSeedColor("40", seedColorHex),
    "35": generateRequiredContrastLevelFromSeedColor("35", seedColorHex),
    "30": generateRequiredContrastLevelFromSeedColor("30", seedColorHex),
    "27": generateRequiredContrastLevelFromSeedColor("27", seedColorHex),
    "25": generateRequiredContrastLevelFromSeedColor("25", seedColorHex),
    "20": generateRequiredContrastLevelFromSeedColor("20", seedColorHex),
    "14": generateRequiredContrastLevelFromSeedColor("14", seedColorHex),
    "10": generateRequiredContrastLevelFromSeedColor("10", seedColorHex),
    "0": generateRequiredContrastLevelFromSeedColor("0", seedColorHex)
  };
}

export function generateSeedColorsFromVariableSeedColors(
  variableSeedColors: VariableSeedColors
): SeedColors {
  const primarySeedHct = Hct.fromInt(argbFromHex(variableSeedColors.primary));

  primarySeedHct.chroma = NEUTRAL_SEED_COLOR_CHROMA;

  const neutralSeedColor = hexFromArgb(primarySeedHct.toInt());

  return {
    ...variableSeedColors,
    neutral: neutralSeedColor,
    neutralMono: FIXED_NEUTRAL_MONOCHROME_SEED_COLOR,
    ...FIXED_UTILITY_SEED_COLORS
  };
}

export function generateUtilityColorsContrastRange(): {
  [color in
    | FixedUtilitySeedColor
    | FixedNeutralMonochromeSeedColor]: ContrastRange;
} {
  return {
    neutralMono: generateContrastRangeFromSeedColor(
      FIXED_NEUTRAL_MONOCHROME_SEED_COLOR
    ),
    magenta: generateContrastRangeFromSeedColor(
      FIXED_UTILITY_SEED_COLORS.magenta
    ),
    violet: generateContrastRangeFromSeedColor(
      FIXED_UTILITY_SEED_COLORS.violet
    ),
    blue: generateContrastRangeFromSeedColor(FIXED_UTILITY_SEED_COLORS.blue),
    azure: generateContrastRangeFromSeedColor(FIXED_UTILITY_SEED_COLORS.azure),
    cyan: generateContrastRangeFromSeedColor(FIXED_UTILITY_SEED_COLORS.cyan),
    green: generateContrastRangeFromSeedColor(FIXED_UTILITY_SEED_COLORS.green),
    chartreuse: generateContrastRangeFromSeedColor(
      FIXED_UTILITY_SEED_COLORS.chartreuse
    ),
    yellow: generateContrastRangeFromSeedColor(
      FIXED_UTILITY_SEED_COLORS.yellow
    ),
    orange: generateContrastRangeFromSeedColor(
      FIXED_UTILITY_SEED_COLORS.orange
    ),
    pink: generateContrastRangeFromSeedColor(FIXED_UTILITY_SEED_COLORS.pink)
  };
}

export function generatePaletteFromSeedColors(seedColors: SeedColors): Palette {
  return {
    primary: generateContrastRangeFromSeedColor(seedColors.primary),
    success: generateContrastRangeFromSeedColor(seedColors.success),
    warning: generateContrastRangeFromSeedColor(seedColors.warning),
    error: generateContrastRangeFromSeedColor(seedColors.error),
    premium: generateContrastRangeFromSeedColor(seedColors.premium),
    neutral: generateContrastRangeFromSeedColor(seedColors.neutral),
    neutralMono: generateContrastRangeFromSeedColor(seedColors.neutralMono),
    magenta: generateContrastRangeFromSeedColor(seedColors.magenta),
    violet: generateContrastRangeFromSeedColor(seedColors.violet),
    blue: generateContrastRangeFromSeedColor(seedColors.blue),
    azure: generateContrastRangeFromSeedColor(seedColors.azure),
    cyan: generateContrastRangeFromSeedColor(seedColors.cyan),
    green: generateContrastRangeFromSeedColor(seedColors.green),
    chartreuse: generateContrastRangeFromSeedColor(seedColors.chartreuse),
    yellow: generateContrastRangeFromSeedColor(seedColors.yellow),
    orange: generateContrastRangeFromSeedColor(seedColors.orange),
    pink: generateContrastRangeFromSeedColor(seedColors.pink)
  };
}

export function generateThemeColorsFromPalette(
  palette: Palette,
  mode: Mode,
  customThemeAssignments?: Partial<ThemeColorAssignments>
): ThemeColors {
  const defaultThemeAssignments =
    mode === "light" ? LIGHT_THEME_ASSIGNMENTS : DARK_THEME_ASSIGNMENTS;
  const themeAssignments = {
    ...defaultThemeAssignments,
    ...customThemeAssignments
  };

  const theme = (Object.keys(themeAssignments) as ColorRole[]).reduce(
    (acc, role) => {
      const seed = themeAssignments[role][0];
      const contrastLevel = themeAssignments[role][1];

      return {
        ...acc,
        [role]: palette[seed][contrastLevel]
      };
    },
    {} as ThemeColors
  );

  return theme;
}
